import { createStore, StoreState } from '../createStore';
import { ClientSyncStores } from './ClientSyncStores';
import { StoreCrypto } from './StoreCrypto';

export interface TinybaseState extends StoreState {
  userStoreCrypto?: StoreCrypto;
  syncStores?: ClientSyncStores;
  initialSyncProgress: InitialSyncProgress;
  hasCompletedInitialSync: boolean;
  propertyDataSyncState: PropertyDataSyncState;
  updateInitialSyncProgress(completedStep: keyof InitialSyncProgress): void;
  createUserStoreCrypto(userId: string, password: string): Promise<void>;
  createSyncStores(userId: string): Promise<void>;
  setHasCompletedInitialSync(value?: boolean): void;
  updatePropertyDataSyncState(update: Partial<PropertyDataSyncState>): void;
}

export type InitialSyncProgress = {
  globalStoreInit: boolean;
  userStoreInit: boolean;
  syncStarted: boolean;
};

export type PropertyDataSyncState = {
  isSyncing?: boolean;
  hasSyncError?: boolean;
  syncTimedOut?: boolean;
};

export type UpdateInitialSyncProgressFn = TinybaseState['updateInitialSyncProgress'];

export const useTinybaseStore = createStore<TinybaseState>(
  'tinybase',
  {
    initialSyncProgress: {
      globalStoreInit: false,
      userStoreInit: false,
      syncStarted: false,
    },
    hasCompletedInitialSync: false,
    propertyDataSyncState: {
      isSyncing: false,
      hasSyncError: false,
      syncTimedOut: false,
    },
  },
  (set, get) => ({
    updateInitialSyncProgress: (completedStep) =>
      set((current) => ({
        initialSyncProgress: { ...current.initialSyncProgress, [completedStep]: true },
      })),

    createUserStoreCrypto: async (userId, password) =>
      set({ userStoreCrypto: await StoreCrypto.createForUserStore(userId, password) }),

    createSyncStores: async (userId) => {
      const { userStoreCrypto, updateInitialSyncProgress } = get();

      if (!userStoreCrypto) {
        throw Error('User store crypto not initialised');
      }

      set({
        syncStores: await ClientSyncStores.create(
          userId,
          userStoreCrypto,
          updateInitialSyncProgress,
        ),
      });
    },

    setHasCompletedInitialSync: (value = true) => set({ hasCompletedInitialSync: value }),

    updatePropertyDataSyncState: (update) =>
      set((current) => ({
        propertyDataSyncState: { ...current.propertyDataSyncState, ...update },
      })),

    cleanup: () => get().syncStores?.destroy(),
  }),
);
